import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Text,
  Link,
  HStack,
  Icon,
  Flex,
  Divider,
  SimpleGrid,
  Heading,
} from "@chakra-ui/react";
import { FaInstagram, FaFacebook } from "react-icons/fa";
import { useLang } from "../../contexts/LangContext";
import { useCategories } from "../../contexts/CategoriesContext";
import PreferredSourceButton from "../common/PreferredSourceButton";

const RED = "#a21b1b";

const FOOTER_CITIES = [
  { label: "Alger", slug: "alger" },
  { label: "Oran", slug: "oran" },
  { label: "Constantine", slug: "constantine" },
  { label: "Annaba", slug: "annaba" },
  { label: "Sétif", slug: "setif" },
  { label: "Blida", slug: "blida" },
];

const FOOTER_BLOG = [
  { label: "Vendre seconde main en Algérie", slug: "vendre-occasion-algerie" },
  { label: "Réussir son vide-dressing", slug: "reussir-vide-dressing-algerie" },
  { label: "Vêtements bébé pas chers", slug: "vetements-bebe-pas-cher-algerie" },
  { label: "Tenues kabyles seconde main", slug: "tenues-kabyles-occasion" },
];

const FOOTER_BRANDS = [
  { label: "Nike Algerie",   slug: "nike" },
  { label: "Adidas Algerie", slug: "adidas" },
  { label: "Zara Algerie",   slug: "zara" },
  { label: "H&M Algerie",    slug: "hm" },
  { label: "Levi's Algerie", slug: "levis" },
];

export default function Footer() {
  const { t } = useLang();
  const { categories } = useCategories();

  return (
    <Box bg="white" borderTop="1px solid" borderColor="gray.100">
      {/* SEO links grid */}
      <Box bg="gray.50" borderTop="1px solid" borderColor="gray.100">
        <Container maxW="container.xl" py={8}>
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={6}>
            {/* Catégories */}
            <Box>
              <Heading as="h3" fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                Catégories
              </Heading>
              <Flex direction="column" gap={2}>
                {categories.slice(0, 6).map((cat) => (
                  <Link key={cat.title} as={RouterLink} to={`/${encodeURIComponent(cat.title)}`}
                    fontSize="sm" color="gray.600" _hover={{ color: RED }} transition="color 0.15s">
                    {cat.title}
                  </Link>
                ))}
              </Flex>
            </Box>

            {/* Villes */}
            <Box>
              <Heading as="h3" fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                Annonces par ville
              </Heading>
              <Flex direction="column" gap={2}>
                {FOOTER_CITIES.map((c) => (
                  <Link key={c.slug} as={RouterLink} to={`/ville/${c.slug}`}
                    fontSize="sm" color="gray.600" _hover={{ color: RED }} transition="color 0.15s">
                    Annonces à {c.label}
                  </Link>
                ))}
              </Flex>
            </Box>

            {/* Marques */}
            <Box>
              <Heading as="h3" fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                Marques
              </Heading>
              <Flex direction="column" gap={2}>
                {FOOTER_BRANDS.map((b) => (
                  <Link key={b.slug} as={RouterLink} to={`/marque/${b.slug}`}
                    fontSize="sm" color="gray.600" _hover={{ color: RED }} transition="color 0.15s">
                    {b.label}
                  </Link>
                ))}
                <Link as={RouterLink} to="/marques"
                  fontSize="sm" color={RED} fontWeight="600" _hover={{ color: "#881616" }}>
                  Toutes les marques →
                </Link>
              </Flex>
            </Box>

            {/* Blog */}
            <Box>
              <Heading as="h3" fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                Blog &amp; Guides
              </Heading>
              <Flex direction="column" gap={2}>
                {FOOTER_BLOG.map((a) => (
                  <Link key={a.slug} as={RouterLink} to={`/blog/${a.slug}`}
                    fontSize="sm" color="gray.600" _hover={{ color: RED }} transition="color 0.15s">
                    {a.label}
                  </Link>
                ))}
                <Link as={RouterLink} to="/blog"
                  fontSize="sm" color={RED} fontWeight="600" _hover={{ color: "#881616" }}>
                  Voir tous les articles →
                </Link>
              </Flex>
            </Box>

            {/* À propos */}
            <Box>
              <Heading as="h3" fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                9achna
              </Heading>
              <Flex direction="column" gap={2}>
                <Link as={RouterLink} to="/about" fontSize="sm" color="gray.600" _hover={{ color: RED }}>À propos</Link>
                <Link as={RouterLink} to="/comment-ca-marche" fontSize="sm" color="gray.600" _hover={{ color: RED }}>Comment ça marche</Link>
                <Link as={RouterLink} to="/contact" fontSize="sm" color="gray.600" _hover={{ color: RED }}>Contact</Link>
                <Link as={RouterLink} to="/help" fontSize="sm" color="gray.600" _hover={{ color: RED }}>Aide</Link>
                <Link as={RouterLink} to="/privacy" fontSize="sm" color="gray.600" _hover={{ color: RED }}>{t("privacy")}</Link>
                <Link as={RouterLink} to="/terms" fontSize="sm" color="gray.600" _hover={{ color: RED }}>{t("terms")}</Link>
              </Flex>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Bottom bar */}
      <Container maxW="container.xl" py={4}>
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={3}
        >
          <Flex align="center" gap={3} flexShrink={0}>
            <Box as="img" src="/logo.png" alt="9achna" h="32px" />
            <Text fontSize="xs" color="gray.500">
              © {new Date().getFullYear()} 9achna — Marketplace Algérie 🇩🇿
            </Text>
          </Flex>

          <HStack spacing={3} flexShrink={0}>
            <Link href="https://www.facebook.com/9achna" isExternal aria-label="Facebook">
              <Icon as={FaFacebook} boxSize={4} color="gray.500" _hover={{ color: RED }} transition="color 0.15s" />
            </Link>
            <Link href="https://www.instagram.com/9achna.dz" isExternal aria-label="Instagram">
              <Icon as={FaInstagram} boxSize={4} color="gray.500" _hover={{ color: RED }} transition="color 0.15s" />
            </Link>
            <Divider orientation="vertical" h="14px" borderColor="gray.200" />
            {/* Google recommande de placer ce bouton avec les autres CTA sociaux */}
            <PreferredSourceButton compact />
            <Divider orientation="vertical" h="14px" borderColor="gray.200" />
            <Text fontSize="xs" color="gray.400">Petites annonces gratuites en Algérie</Text>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}
